import { useEffect, useMemo, useState } from "react";
import { api, apiForm } from "./lib/api";
import { FileButton } from "./components/ui";

// Company Portal — Import / Vault (staff-only). Surfaces the existing /import backend:
// pick a file, map its columns to Orator's canonical fields (auto-suggested), preview, then
// load into the shared voter vault. Recent jobs poll below. Large state files land later on
// the async pipeline (this MVP posts the file inline, fine for universe-sized loads).
const BORDER = "1px solid rgba(128,128,128,.35)";
type Field = { key: string; label: string; group: string; key_for_update?: boolean };
type Profile = { file: string; name: string; state: string | null; fields: string[] };
type Job = {
  id: string; source_type: string; file_name: string | null; mode: string; profile: string | null;
  status: string; rows_in: number | null; rows_ok: number | null; rows_err: number | null;
  created_at: string | null; imported_by?: string | null;
  progress?: number | null; result?: any; errors?: any;
};

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
// header aliases -> canonical field key (auto-map heuristic; user can override)
const ALIASES: Record<string, string> = {
  vuid: "external_voter_id", voterid: "external_voter_id", statevoterid: "external_voter_id", sos: "external_voter_id",
  first: "first_name", firstname: "first_name", fname: "first_name",
  middle: "middle_name", mi: "middle_name",
  last: "last_name", lastname: "last_name", lname: "last_name", surname: "last_name",
  suffix: "name_suffix", namesuffix: "name_suffix",
  dob: "dob", birthdate: "dob", dateofbirth: "dob",
  gender: "gender", sex: "gender",
  resaddress: "street", address: "street", street: "street", resstreet: "street",
  rescity: "city", city: "city",
  resstate: "state", state: "state",
  reszip: "zip", zip: "zip", zipcode: "zip", zip5: "zip",
  lat: "house_lat", latitude: "house_lat",
  lon: "house_lon", lng: "house_lon", longitude: "house_lon",
  precinct: "precinct", pct: "precinct",
  county: "county",
  cd: "cd", congressional: "cd", congressionaldistrict: "cd",
  sd: "sd", statesenate: "sd",
  hd: "hd", statehouse: "hd",
  regstatus: "registration_status", registrationstatus: "registration_status",
  regdate: "registration_date", registrationdate: "registration_date",
  party: "registered_party", registeredparty: "registered_party",
  phone: "phone", homephone: "phone",
  cell: "cell_phone", cellphone: "cell_phone", mobile: "cell_phone",
  modeledparty: "modeled_party", partisanship: "modeled_party",
  propensity: "modeled_propensity", modeledpropensity: "modeled_propensity",
  affinity: "modeled_affinity", modeledaffinity: "modeled_affinity",
};

function parseCsv(text: string, delim: string, maxRows: number): { headers: string[]; rows: string[][] } {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.length > 0);
  if (!lines.length) return { headers: [], rows: [] };
  const split = (line: string) => {
    const out: string[] = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (q) { if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; }
      else { if (c === '"') q = true; else if (c === delim) { out.push(cur); cur = ""; } else cur += c; }
    }
    out.push(cur); return out.map((x) => x.trim());
  };
  const headers = split(lines[0]);
  const rows = lines.slice(1, 1 + maxRows).map(split);
  return { headers, rows };
}

export function ImportVault({ onGo }: { onGo?: (tab: string) => void }) {
  const [fields, setFields] = useState<Field[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [mode, setMode] = useState<"upsert" | "insert" | "update">("upsert");
  const [sourceType, setSourceType] = useState("state_file");
  const [stateCode, setStateCode] = useState("TX");
  const [name, setName] = useState("");
  const [delim, setDelim] = useState(",");
  const [profileFile, setProfileFile] = useState("");   // "" = manual mapping

  const [fileName, setFileName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});   // field.key -> header

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  const [validating, setValidating] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  // "No imports yet." used to render before the first fetch resolved, so a portal with a full import
  // history briefly claimed it had none. jobsLoaded flips once, after the first attempt.
  const [jobsLoaded, setJobsLoaded] = useState(false);   // loading-ok: first-paint guard, paired with jobsErr so a failed fetch never reads as "no imports yet"
  const [fileObj, setFileObj] = useState<File | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [fieldsErr, setFieldsErr] = useState<string | null>(null);
  const [jobsErr, setJobsErr] = useState<string | null>(null);

  useEffect(() => {
    // THE FIELD CATALOGUE IS LOAD-BEARING, not decoration. The mapping shown on screen is built
    // from it, but `mapping` itself comes from the ALIASES heuristic in onFile — so when this call
    // failed, step 2 rendered as an empty box reading "0 mapped" while `mappedCount` was non-zero
    // and the run button stayed live. Pressing it wrote to the SHARED VAULT using a column mapping
    // the operator had never been shown. Track the failure and block the run.
    setFieldsErr(null);
    api<{ fields: Field[] }>("/import/fields").then((r) => setFields(r.fields || []))
      .catch((e) => setFieldsErr(String(e?.message || e)));
    api<{ profiles: Profile[] }>("/import/profiles").then((r) => setProfiles(r.profiles || [])).catch(() => {});
    loadJobs();
  }, []);

  async function loadJobs() {
    // A failed poll used to be silent, so a running import could stall on "Importing…" with the
    // progress bar frozen and nothing to explain it; and a failed first load rendered a confident
    // "No imports yet."
    try { setJobs((await api<{ jobs: Job[] }>("/import/jobs?limit=15")).jobs || []); setJobsErr(null); }
    catch (e: any) { setJobsErr(String(e?.message || e)); }
    finally { setJobsLoaded(true); }
  }
  // poll while a job is running/queued (or we're tracking one)
  useEffect(() => {
    const running = !!activeJobId || jobs.some((j) => j.status === "running" || j.status === "queued");
    if (!running) return;
    const t = setInterval(loadJobs, 2500);
    return () => clearInterval(t);
  }, [jobs, activeJobId]);
  // react to the tracked job finishing
  useEffect(() => {
    if (!activeJobId) return;
    const j = jobs.find((x) => x.id === activeJobId);
    if (!j) return;
    if (j.status === "done") {
      setResult(j.result || null); setBusy(false); setActiveJobId(null);
      setMsg(`Imported ${j.rows_ok ?? "?"} of ${j.rows_in ?? "?"} rows into the vault.`);
    } else if (j.status === "error") {
      setErr((j.errors && (j.errors.error || j.errors.note)) || "Import failed."); setBusy(false); setActiveJobId(null);
    }
  }, [jobs, activeJobId]);

  function onFile(f: File) {
    setFileName(f.name); setFileObj(f); setResult(null); setReport(null); setErr(null); setMsg(null); setActiveJobId(null);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setCsvText(text);
      const guessDelim = text.slice(0, 2000).includes("\t") && !text.slice(0, 2000).includes(",") ? "\t" : delim;
      setDelim(guessDelim);
      const { headers, rows } = parseCsv(text, guessDelim, 12);
      setHeaders(headers); setRows(rows);
      // auto-map
      const m: Record<string, string> = {};
      for (const h of headers) {
        const key = ALIASES[norm(h)];
        if (key && !Object.values(m).includes(h)) m[key] = h;
      }
      setMapping(m);
    };
    reader.readAsText(f);
  }

  const grouped = useMemo(() => {
    const g: Record<string, Field[]> = {};
    for (const f of fields) (g[f.group] ||= []).push(f);
    return g;
  }, [fields]);
  const mappedCount = Object.values(mapping).filter(Boolean).length;
  const bigFile = csvText.length > 15_000_000;
  const canRun = !!fileObj && !busy && !activeJobId && !fieldsErr && (profileFile ? true : mappedCount > 0) &&
    (mode !== "update" || profileFile ? true : !!mapping.external_voter_id);
  const activeJob = activeJobId ? jobs.find((j) => j.id === activeJobId) : jobs.find((j) => j.status === "running" || j.status === "queued");

  async function validate() {
    setValidating(true); setErr(null); setReport(null);
    try {
      const body: any = { csv_text: csvText, mode, delimiter: delim };
      if (profileFile) body.profile_file = profileFile;
      else body.columns = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      setReport(await api<any>("/import/validate", { method: "POST", body: JSON.stringify(body) }));
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setValidating(false); }
  }

  async function run() {
    if (!fileObj) { setErr("Choose a file first."); return; }
    setBusy(true); setErr(null); setMsg(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("mode", mode);
      fd.append("source_type", sourceType);
      fd.append("delimiter", delim);
      if (profileFile) fd.append("profile_file", profileFile);
      else {
        fd.append("columns", JSON.stringify(Object.fromEntries(Object.entries(mapping).filter(([, v]) => v))));
        fd.append("state", stateCode);
        fd.append("name", name || fileName || "Universe upload");
      }
      const r = await apiForm<{ job_id: string }>("/import/start", fd);
      setActiveJobId(r.job_id);
      setMsg("Upload received — importing in the background…");
      loadJobs();
    } catch (e: any) { setErr(String(e.message || e)); setBusy(false); }
  }

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>Import / Vault</h2>
        <span className="muted">Load a voter file into the shared base vault</span>
        <button className="btn sm" style={{ marginLeft: "auto" }} onClick={loadJobs}>Refresh jobs</button>
      </div>
      {err && <div className="err" style={{ marginBottom: 10 }}>{err}</div>}
      {msg && <div className="muted" style={{ marginBottom: 10, fontSize: 13, color: "#3fc079", fontWeight: 600 }}>{msg}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
        <div className="card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div className="fld" style={{ marginBottom: 6 }}>1 · Choose a file</div>
            <FileButton accept=".csv,.tsv,.txt" onPick={(fs) => onFile(fs[0])} formats="CSV or TSV"
              file={fileName || null} note={headers.length ? `${headers.length} columns` : null} />
            {/* The background upload path SHIPPED — /import/start streams to disk and runs on a worker
                    thread — so the old "coming soon" warning was telling staff about a limitation that
                    no longer exists. What is still true is that Validate posts the whole file as JSON,
                    which is the part that struggles on a full state file. */}
                {bigFile && <div className="muted" style={{ fontSize: 12, marginTop: 6, color: "#e08b3f" }}>Large file — the import itself runs in the background and is fine; <strong>Validate</strong> reads the whole file in the browser and may be slow or fail. You can import without validating.</div>}
          </div>

          <div className="grid2">
            <label className="fld" style={{ fontSize: 12 }}>Mapping
              <select className="ctl" value={profileFile} onChange={(e) => setProfileFile(e.target.value)}>
                <option value="">Map columns manually</option>
                {profiles.map((p) => <option key={p.file} value={p.file}>{p.name}</option>)}
              </select>
            </label>
            <label className="fld" style={{ fontSize: 12 }}>Write mode
              <select className="ctl" value={mode} onChange={(e) => setMode(e.target.value as any)}>
                <option value="upsert">Upsert (add + update)</option>
                <option value="insert">Insert only (new rows)</option>
                <option value="update">Update by VUID (enrich)</option>
              </select>
            </label>
          </div>
          <div className="grid2">
            <label className="fld" style={{ fontSize: 12 }}>State<input className="ctl" value={stateCode} maxLength={2} onChange={(e) => setStateCode(e.target.value.toUpperCase())} /></label>
            <label className="fld" style={{ fontSize: 12 }}>Load name<input className="ctl" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. TX SoS Jun 2026" /></label>
          </div>

          {!profileFile && headers.length > 0 && (
            <div>
              <div className="fld" style={{ marginBottom: 6 }}>2 · Map columns <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· {mappedCount} mapped (auto-suggested)</span></div>
              <div className="card" style={{ padding: 0, maxHeight: 320, overflow: "auto", background: "transparent" }}>
                {Object.entries(grouped).map(([group, gf]) => (
                  <div key={group}>
                    <div style={{ padding: "5px 10px", borderTop: BORDER, fontSize: 11, fontWeight: 700, opacity: 0.7 }}>{group}</div>
                    {gf.map((f) => (
                      <div key={f.key} className="row" style={{ gap: 8, padding: "3px 10px", alignItems: "center" }}>
                        <span style={{ flex: 1, fontSize: 13 }}>{f.label}{f.key_for_update && mode === "update" ? " *" : ""}</span>
                        <select className="ctl" style={{ maxWidth: 190 }} value={mapping[f.key] || ""}
                          onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}>
                          <option value="">—</option>
                          {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {mode === "update" && !mapping.external_voter_id && <div className="err" style={{ marginTop: 6, fontSize: 12 }}>To update by State Voter ID, choose which column holds it.</div>}
            </div>
          )}
          {profileFile && <div className="muted" style={{ fontSize: 12 }}>Using the saved “{profiles.find((p) => p.file === profileFile)?.name}” mapping — column mapping is taken from the profile.</div>}

          {fieldsErr && (
            <div className="err" style={{ fontSize: 12 }}>
              Couldn't load the field catalogue, so the column mapping above can't be shown or checked — importing is
              blocked until it loads. Reload the page to retry. ({fieldsErr})
            </div>
          )}
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" disabled={!csvText || validating} onClick={validate}>{validating ? "Checking…" : "Validate"}</button>
            <button className="btn accent" disabled={!canRun} onClick={run}>{activeJobId ? "Importing…" : "Import into vault"}</button>
          </div>
          {activeJob && (activeJob.status === "running" || activeJob.status === "queued") && (
            <div className="card" style={{ padding: 10 }}>
              <div className="row" style={{ justifyContent: "space-between", fontSize: 12 }}>
                <span className="muted">{activeJob.status === "queued" ? "Queued…" : "Importing into the vault…"}</span>
                <span className="muted">{activeJob.progress ?? 0}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 5, background: "rgba(128,128,128,.25)", marginTop: 6, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${activeJob.progress ?? 0}%`, background: "#3fc079", transition: "width .3s" }} />
              </div>
            </div>
          )}
          {report && (
            <div className="card" style={{ padding: 10, borderLeft: `3px solid ${report.ok ? "#3fc079" : "#e0553f"}` }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{report.ok ? "Looks good to import" : "Fix these before importing"}
                <span className="muted" style={{ fontWeight: 400 }}> · {report.rows.toLocaleString()} rows · {report.mapped_fields.length} fields mapped</span></div>
              {report.issues.length === 0 && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>No issues found.</div>}
              {report.issues.map((it: any, i: number) => (
                <div key={i} className="row" style={{ gap: 6, fontSize: 12, marginTop: 4, alignItems: "baseline" }}>
                  <span style={{ color: it.severity === "error" ? "#e0553f" : "#e08b3f", fontWeight: 700 }}>{it.severity === "error" ? "✕" : "⚠"}</span>
                  <span>{it.label}{it.count ? ` (${it.count})` : ""}{it.sample?.length ? <span className="muted"> · e.g. line {it.sample.join(", ")}</span> : null}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {headers.length > 0 && (
            <div className="card" style={{ padding: 0, overflow: "auto" }}>
              <div style={{ padding: "8px 12px", borderBottom: BORDER, fontWeight: 700 }}>Preview <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· first {rows.length} rows</span></div>
              <div style={{ overflow: "auto", maxHeight: 220 }}>
                <table style={{ fontSize: 12 }}>
                  <thead><tr>{headers.map((h) => <th key={h} style={{ whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
                  <tbody>{rows.map((r, i) => <tr key={i}>{headers.map((_, j) => <td key={j} className="muted" style={{ whiteSpace: "nowrap" }}>{r[j] ?? ""}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
          {result && (
            <div className="card" style={{ padding: 12, borderLeft: "3px solid #3fc079" }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Import complete</div>
              <div className="row" style={{ gap: 14, flexWrap: "wrap", fontSize: 13 }}>
                <span><b>{(result.rows_in ?? 0).toLocaleString()}</b> rows in</span>
                <span style={{ color: "#3fc079" }}><b>{(result.voters_new ?? 0).toLocaleString()}</b> new</span>
                <span><b>{(result.voters_updated ?? 0).toLocaleString()}</b> updated</span>
                {result.voters_skipped ? <span className="muted"><b>{result.voters_skipped.toLocaleString()}</b> skipped</span> : null}
                <span className="muted"><b>{(result.households_new ?? 0).toLocaleString()}</b> new households</span>
              </div>
              {result.geocode_needed > 0 && (
                <div className="row" style={{ gap: 10, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, color: "#e08b3f" }}>⚠ {result.geocode_needed.toLocaleString()} new addresses need geocoding.</span>
                  {onGo && <button className="btn sm accent" onClick={() => onGo("geocode")}>Geocode these →</button>}
                </div>
              )}
            </div>
          )}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: "8px 12px", borderBottom: BORDER, fontWeight: 700 }}>Recent loads</div>
            <table style={{ fontSize: 12 }}>
              <thead><tr><th>When</th><th>File</th><th>Mode</th><th>Status</th><th>Rows</th></tr></thead>
              <tbody>
                {!jobsLoaded && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
                {jobsLoaded && jobs.length === 0 && (
                  <tr><td colSpan={5} className="muted" style={jobsErr ? { color: "var(--warn,#eab308)" } : undefined}>
                    {jobsErr ? `Couldn't load imports — ${jobsErr}` : "No imports yet."}
                  </td></tr>
                )}
                {jobs.map((j) => (
                  <tr key={j.id}>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{j.created_at ? new Date(j.created_at).toLocaleString() : "—"}</td>
                    <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.file_name || "—"}</td>
                    <td>{j.mode}</td>
                    <td><span className={"badge " + (j.status === "done" ? "active" : j.status === "error" ? "closed" : "")}>{j.status}</span></td>
                    <td className="muted">{j.rows_ok ?? "—"}/{j.rows_in ?? "—"}{j.rows_err ? ` · ${j.rows_err} err` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
